import { Controller } from "@hotwired/stimulus"

// QAN-698: when a new product is filed under an existing bank (BL####), pours
// that bank's states, counties, bureau and branch-locator URL into the visible
// form controls so they aren't re-typed for every product.
//
// It fills the *controls*, not the record — every carried value stays editable
// before the save (AC #2), and nothing on the server copies these columns. The
// same reason rules out a before_validation copy: bureau_pulled is NOT NULL with
// no blank option in the select, so a "fill when blank" could never fire for it,
// and an empty states_served is a real answer ("serves all states") that a
// server-side fill would make impossible to express.
//
// Attached on the create page only (see _form.html.erb) — the edit page reuses
// the same picker to re-bank a product, where repainting these four fields would
// be silent data loss.
export default class extends Controller {
  static targets = [
    "states", "statesHint",
    "counties", "countiesHint",
    "bureau", "bureauHint",
    "branchLocator", "branchLocatorHint"
  ]
  static values = { url: String }

  connect() {
    // Whatever the admin had before any bank was picked, restored when they
    // switch away from "existing". Deliberately NOT a prefill: on a validation
    // failure the form re-renders with the bank already selected and the posted
    // values back in the fields, so filling on connect would overwrite the
    // admin's own work with the bank's.
    this.baseline = {
      states_served: this.selectedStates(),
      counties: this.selectedCounties(),
      bureau_pulled: this.bureauTarget.value,
      branch_locator_url: this.branchLocatorTarget.value
    }

    // Guards against a slow response for bank A landing after a later pick of
    // bank B (or after a switch away from "existing") and repainting the form.
    this.requestSeq = 0
  }

  apply(event) {
    const { mode, externalId } = event.detail
    const seq = ++this.requestSeq

    if (mode !== "existing" || !externalId) {
      this.fill(this.baseline)
      this.toggleHints(false)
      return
    }

    this.load(externalId, seq)
  }

  async load(externalId, seq) {
    try {
      const url = `${this.urlValue}?lender_external_id=${encodeURIComponent(externalId)}`
      // No X-CSRF-Token: this is a GET and isn't forgery-protected.
      const response = await fetch(url, {
        credentials: "same-origin",
        headers: { "Accept": "application/json" }
      })

      // A content-type check, not just response.ok: the controller's role guard
      // redirects to HTML rather than rendering JSON, and fetch follows the 302,
      // so an ok response can still be a page.
      const contentType = response.headers.get("content-type") || ""
      if (!response.ok || !contentType.includes("application/json")) {
        this.revert(seq)
        return
      }

      const data = await response.json()
      if (seq !== this.requestSeq) return

      // Sanitized here rather than in fill(), so the baseline — the admin's own
      // typing — is restored untouched even when it is itself unsubmittable.
      data.branch_locator_url = this.submittableUrl(data.branch_locator_url)

      this.fill(data)
      this.toggleHints(true)
    } catch (error) {
      // Silent by design: prefill is a convenience, so a failure never interrupts
      // the admin — but it does have to undo itself, see revert().
      this.revert(seq)
    }
  }

  // A failed load degrades to "no prefill", never to "the previous bank's prefill".
  // Picking bank A and then bank B with the second request failing would otherwise
  // leave A's geography in the form under a hint reading "from selection above",
  // attributing it to B — which is the bank that then gets saved.
  //
  // Seq-guarded like the success path: a stale failure must not wipe a newer fill.
  revert(seq) {
    if (seq !== this.requestSeq) return

    this.fill(this.baseline)
    this.toggleHints(false)
  }

  fill(data) {
    this.setStates(data.states_served || [])

    // The form's inline geography script rebuilds the county <option> list from
    // GeoReference whenever the states select changes, so the counties have to be
    // set after that has run. Event listeners fire synchronously, so the new
    // options exist by the time dispatchEvent returns — no timer needed, and the
    // county data isn't duplicated here.
    this.statesTarget.dispatchEvent(new Event("change", { bubbles: true }))

    this.setCounties(data.counties || [])
    this.setBureau(data.bureau_pulled)
    this.branchLocatorTarget.value = data.branch_locator_url || ""
  }

  // Case-insensitive: saves through the model upcase state codes, but CSV
  // imports and update_columns bypass that normalization.
  setStates(states) {
    const wanted = states.map(state => String(state).toUpperCase())
    Array.from(this.statesTarget.options).forEach(option => {
      option.selected = wanted.includes(option.value.toUpperCase())
    })
  }

  setCounties(counties) {
    const wanted = counties.map(county => String(county))
    const offered = new Set()

    Array.from(this.countiesTarget.options).forEach(option => {
      option.selected = wanted.includes(option.value)
      offered.add(option.value)
    })

    // A carried county the rebuilt list doesn't offer predates the GeoReference
    // data; append it so copying a bank can't quietly drop part of its coverage.
    // createElement + textContent, never innerHTML: counties are permitted
    // straight through as free text with no validation.
    wanted.forEach(county => {
      if (offered.has(county)) return
      const option = document.createElement("option")
      option.value = county
      option.textContent = county
      option.selected = true
      this.countiesTarget.appendChild(option)
    })
  }

  // branch_locator_url lands in a url_field — type="url" — so a value the browser
  // judges invalid blocks the save with a native message and no visible cause. The
  // column has no validation or normalization, so a scheme-less imported value is
  // possible; carry it only if it can actually be submitted. Same rule as
  // setBureau: never paste a value the control can't hold.
  submittableUrl(value) {
    const url = String(value || "")
    if (url === "") return ""

    try {
      new URL(url)
      return url
    } catch (error) {
      return ""
    }
  }

  // Only assign a value the select actually offers: an unknown one would blank a
  // NOT NULL control, and the save would fail with nothing visibly wrong.
  setBureau(bureau) {
    const value = String(bureau)
    const offered = Array.from(this.bureauTarget.options).some(option => option.value === value)
    if (offered) this.bureauTarget.value = value
  }

  toggleHints(visible) {
    [
      this.statesHintTarget,
      this.countiesHintTarget,
      this.bureauHintTarget,
      this.branchLocatorHintTarget
    ].forEach(hint => hint.classList.toggle("hidden", !visible))
  }

  selectedStates() {
    return Array.from(this.statesTarget.selectedOptions).map(option => option.value)
  }

  selectedCounties() {
    return Array.from(this.countiesTarget.selectedOptions).map(option => option.value)
  }
}
